import Vue from 'vue'
import Router from 'vue-router'
import routes from './routes'
import axiosInstance from '../plugins/axios'
import {
  isUatAdminEnvironment,
  performImmediateUatAdminLogout
} from '../utils/enforceUatAdminDeprecation'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes
})

// Verify session via the backend API when localStorage doesn't have isAuthenticated.
// The backend sets an httpOnly session cookie on .myfinfi.com at login time.
// That cookie is sent automatically by the browser to any *.myfinfi.com subdomain,
// so we can verify it without any manual cookie/localStorage state transfer between apps.
async function verifySession() {
  try {
    const res = await axiosInstance.get('/cualitywork/auth-user')
    if (res.data) {
      localStorage.setItem('isAuthenticated', 'true')
      // Rebuild user in localStorage from fresh API response
      const userData = { ...res.data }
      localStorage.setItem('user', JSON.stringify(userData))
      // Rebuild finfi._vuex so Vuex picks up permissions on this navigation
      const vuexState = {
        wfmPermissions: userData.wfmPermissions || [],
        wfmStatements: userData.wfmStatements || [],
        useCase: [],
        subCases: [],
        selectValue: null,
        inputValue: null,
      }
      localStorage.setItem('finfi._vuex', JSON.stringify(vuexState))
      return true
    }
  } catch (_) {
    // 401 or network error → treat as not authenticated
  }
  return false
}

router.beforeEach(async (to, from, next) => {
  // Enforce UAT Admin deprecation: block access and perform immediate logout
  if (isUatAdminEnvironment()) {
    performImmediateUatAdminLogout()
    if (to.path !== '/uatadmin-disabled') {
      next({ path: '/uatadmin-disabled' })
      return
    }
    next()
    return
  }

  let isAuthenticated = Vue.prototype.$storage?.getUniversal('isAuthenticated')
  const needsInitialSetup = localStorage.getItem('needsInitialSetup')
  const hasCompletedSetup = Vue.prototype.$storage?.getUniversal('hasCompletedSetup')

  // If the route needs auth and we don't have it in localStorage,
  // try verifying the backend session before giving up and redirecting to login.
  if (to.meta.private && !isAuthenticated) {
    const sessionValid = await verifySession()
    if (sessionValid) {
      isAuthenticated = true
    } else {
      next({ name: 'Login' })
      return
    }
  }

  if (isAuthenticated && to.meta.private) {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const userRoles = Array.isArray(user.role) ? user.role : []
    const isAdmin = userRoles.includes('WFM_ADMIN') || userRoles.includes('WFM_ACCESS')
    const appMode = localStorage.getItem('app_mode') || (isAdmin ? 'admin' : 'employee')

    // 1. Check Admin Portal Segregation
    if (to.meta.portal === 'admin' || to.meta.roleScope === 'admin') {
      // If user is employee or in employee mode, forbid access to Admin portal
      if (!isAdmin || appMode === 'employee') {
        next({ name: 'AppDashboard' })
        return
      }
    }

    // 2. Check Employee App Portal Segregation
    if (to.meta.portal === 'app' || to.meta.roleScope === 'employee') {
      // If user is in admin mode or has only admin scope, prevent landing in app screen
      if (appMode === 'admin' && isAdmin) {
        next({ name: 'Dashboard' })
        return
      }
    }
  }

  if (
    isAuthenticated &&
    needsInitialSetup === 'true' &&
    !hasCompletedSetup &&
    to.name !== 'SetupPage'
  ) {
    next({ name: 'SetupPage' })
  } else if (to.meta.public && isAuthenticated) {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const userRoles = Array.isArray(user.role) ? user.role : []
    const isAdmin = userRoles.includes('WFM_ADMIN') || userRoles.includes('WFM_ACCESS')
    const appMode = localStorage.getItem('app_mode') || (isAdmin ? 'admin' : 'employee')

    if (appMode === 'employee' || !isAdmin) {
      next({ name: 'AppDashboard' })
    } else {
      next({ name: 'Dashboard' })
    }
  } else if (
    isAuthenticated &&
    to.name === 'SetupPage' &&
    (!needsInitialSetup || needsInitialSetup !== 'true' || hasCompletedSetup)
  ) {
    next({ name: 'Dashboard' })
  } else {
    next()
  }
})

router.afterEach((to) => {
  // If running inside an iframe (embedded in the mobile app),
  // notify the parent so it can persist our current path.
  // This lets the mobile app restore us to this exact page on refresh.
  try {
    if (window.self !== window.top) {
      window.parent.postMessage(
        { type: 'ADMIN_ROUTE_CHANGE', path: to.fullPath },
        '*'
      )
    }
  } catch (_) {
    // cross-origin access blocked — safe to ignore
  }
})

export default router
